import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  ClipboardList, BarChart3, Users, ShieldOff, Trophy,
  FileEdit, Radio, Archive, Globe, Lock, Activity
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAdminAuth } from '../../components/admin/RequireAuth';
import { adminDashboardAPI } from '../../utils/adminDashboardAPI';
import { ACAO_LABEL } from '../../utils/auditLabels';

const saudacao = () => {
  const hora = new Date().getHours();
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
};

const StatCard = ({ icon: Icone, cor, titulo, valor, chips, delay, indicadorAtivo }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 p-6 animate-fade-in-up"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}>
        <Icone className="w-5 h-5 text-white" />
        {indicadorAtivo && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
        )}
      </div>
      <p className="text-sm font-medium text-gray-500">{titulo}</p>
    </div>
    <p className="text-3xl font-bold text-gray-900 mb-3">{valor}</p>
    {chips && (
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <span key={chip.label} className="text-xs font-medium bg-gray-50 text-gray-600 px-2.5 py-1 rounded-full flex items-center gap-1">
            <chip.icon className="w-3 h-3" /> {chip.valor} {chip.label}
          </span>
        ))}
      </div>
    )}
  </div>
);

const DashboardHome = () => {
  const sessao = useAdminAuth();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    adminDashboardAPI.obterResumo()
      .then(setDados)
      .catch((error) => toast.error(error.userMessage || 'Erro ao carregar o dashboard'))
      .finally(() => setCarregando(false));
  }, []);

  return (
    <AdminLayout title="Dashboard">
      <Toaster position="top-center" />

      <div className="mb-8 animate-fade-in-up">
        <h2 className="text-2xl font-bold text-gray-900">
          {saudacao()}, {sessao?.nome_completo?.split(' ')[0] || sessao?.nome_completo}
        </h2>
        <p className="text-gray-500 mt-1">
          Aqui está um resumo do que está acontecendo nas Enquetes públicas.
        </p>
      </div>

      {carregando && (
        <div className="flex justify-center py-16">
          <div className="loading-spinner" />
        </div>
      )}

      {!carregando && dados && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <StatCard
              icon={ClipboardList}
              cor="bg-primary-600"
              titulo="Enquetes"
              valor={dados.enquetes.total}
              delay={0}
              chips={[
                { icon: FileEdit, valor: dados.enquetes.rascunho, label: 'rascunho' },
                { icon: Radio, valor: dados.enquetes.publicada, label: 'publicada' },
                { icon: Archive, valor: dados.enquetes.encerrada, label: 'encerrada' }
              ]}
            />
            <StatCard
              icon={BarChart3}
              cor="bg-[#40A1E6]"
              titulo="Média de votos por enquete"
              valor={dados.votos.media_por_enquete.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
              delay={0.08}
              chips={[
                { icon: Activity, valor: dados.votos.dinamicas.toLocaleString('pt-BR'), label: 'votos em enquetes novas' },
                { icon: Archive, valor: dados.votos.portal.toLocaleString('pt-BR'), label: 'Portal de Entrada' },
                { icon: Archive, valor: dados.votos.telao_brasil.toLocaleString('pt-BR'), label: 'Telão do Brasil' }
              ]}
            />
            <StatCard
              icon={Users}
              cor="bg-emerald-500"
              titulo="Usuários online agora"
              valor={dados.usuarios.online}
              indicadorAtivo={dados.usuarios.online > 0}
              delay={0.16}
              chips={[
                { icon: Users, valor: dados.usuarios.total, label: 'no total' },
                { icon: Activity, valor: dados.usuarios.ativos, label: 'ativos' }
              ]}
            />
            {dados.seguranca ? (
              <StatCard
                icon={ShieldOff}
                cor="bg-red-500"
                titulo="IPs bloqueados"
                valor={dados.seguranca.ips_bloqueados}
                delay={0.24}
                chips={[
                  { icon: Globe, valor: dados.enquetes.publicas, label: 'enquetes públicas' },
                  { icon: Lock, valor: dados.enquetes.privadas, label: 'privadas' }
                ]}
              />
            ) : (
              <StatCard
                icon={Globe}
                cor="bg-amber-500"
                titulo="Visibilidade"
                valor={dados.enquetes.publicas}
                delay={0.24}
                chips={[
                  { icon: Globe, valor: dados.enquetes.publicas, label: 'públicas' },
                  { icon: Lock, valor: dados.enquetes.privadas, label: 'privadas' }
                ]}
              />
            )}
          </div>

          <div className={`grid grid-cols-1 ${dados.atividade_recente ? 'lg:grid-cols-2' : ''} gap-6`}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 animate-fade-in-up" style={{ animationDelay: '0.32s' }}>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> Enquetes mais votadas
              </h3>
              {dados.ranking.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">Ainda não há votos registrados.</p>
              ) : (
                <div className="space-y-4">
                  {dados.ranking.map((item, i) => {
                    const maxVotos = dados.ranking[0].votos;
                    const largura = Math.max(8, Math.round((item.votos / maxVotos) * 100));
                    return (
                      <div key={item.slug} className="flex items-center gap-3">
                        <span
                          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-700 font-medium truncate pr-2 flex items-center gap-1.5">
                              {item.titulo}
                              {item.legado && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                  legado
                                </span>
                              )}
                            </span>
                            <span className="text-gray-900 font-semibold flex-shrink-0">{item.votos.toLocaleString('pt-BR')}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full transition-all duration-700 ${
                                i === 0 ? 'bg-amber-400' : item.legado ? 'bg-gray-400' : 'bg-[#40A1E6]'
                              }`}
                              style={{ width: `${largura}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {dados.atividade_recente && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-600" /> Atividade recente
                </h3>
                {dados.atividade_recente.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">Nenhuma ação registrada ainda.</p>
                ) : (
                  <div className="space-y-3.5">
                    {dados.atividade_recente.map((entrada) => (
                      <div key={entrada.id} className="flex gap-3 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#40A1E6] mt-1.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-gray-700">
                            <span className="font-semibold">{entrada.admin_username}</span>{' '}
                            {ACAO_LABEL[entrada.acao] || entrada.acao}
                            {entrada.entidade_id ? ` #${entrada.entidade_id}` : ''}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(entrada.data_criacao).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default DashboardHome;
